import { FC, useRef, useState } from "react";
import styles from "./DropDownPrice.module.scss";
import Nouislider from "nouislider-react";
import "nouislider/distribute/nouislider.css";
import { useTranslation } from "next-i18next";
import { Button, ToggleButton } from "../Buttons";
import { useAppSelector } from "@/lib/store/app";
import { BahtSign, DollarSign, RubSign } from "../../icons/currency";
import useOnClickOutside from "@/lib/hooks/useOnClickOutside";
import { PriceInput } from "../Input";
import { P2 } from "../Typography";
import { toMoney } from "@/lib/utils/toMoney";
import { ICurrency } from "@/lib/types/currency";
import { useCatalogFilter } from "@/lib/hooks/useCatalogFilter";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { NumericFormat } from "react-number-format";
import cl from "classnames";
import { ParamRecord } from "@/lib/types/catalogPage";
import { predefinedPrices } from "../../blocks/page-specific/Catalog/CatalogFilter/constants";

interface IDropDownPrice {
  min: number;
  max: number;
  currency: ICurrency["value"];
  symbol: ICurrency["symbol"];
  isInModal?: boolean;
}

const currencyIcon = {
  usd: <DollarSign />,
  rub: <RubSign />,
  thb: <BahtSign />,
};

export const DropDownPrice: FC<{ isFixed: boolean }> = ({ isFixed }) => {
  const [min, max] = useAppSelector((state) => state.catalog.prices);
  const [filterMin, filterMax] = useAppSelector((state) => state.catalog.filteredPrices);
  const [isOpen, setIsOpen] = useState(false);
  const priceRef = useRef(null);
  const { value: currency, symbol } = useAppSelector((state) => state.settings.currency);
  const { query } = useCatalogFilter();
  const { minPrice: queryMin, maxPrice: queryMax } = query;

  const { t } = useTranslation("index");

  const from = ((queryMin ? +queryMin : filterMin) / 1_000_000)
    .toFixed(2)
    .replace(".", ",");
  const to = ((queryMax ? +queryMax : filterMax) / 1_000_000)
    .toFixed(2)
    .replace(".", ",");

  const label = `${t("prepositions.from")} ${from === "0,00" ? "0,01" : from} ${t(
    "prepositions.to",
  )} ${to === "0,0" ? "0,01" : to} ${t("prepositions.million")}`;

  const toggle = () => setIsOpen((c) => !c);

  useOnClickOutside(priceRef, () => setIsOpen(false));

  const hasDot =
    (!!query.minPrice && +query.minPrice !== min) ||
    (!!query.maxPrice && +query.maxPrice !== max);

  return (
    <div className={styles.price} ref={priceRef}>
      <Button
        label={label}
        suppressHydrationWarning
        theme="grayBorder"
        size="lg"
        as="button"
        className={cl(
          styles.price__toggle,
          hasDot && styles["price__toggle--dot"],
          isFixed && styles["price__smallButton"],
        )}
        // icon={currencyIcon[currency]}
        onClick={toggle}
      />
      <div className={cl({ [styles["hidden"]]: !isOpen })}>
        <PriceControls max={max} min={min} currency={currency} symbol={symbol} />
      </div>
    </div>
  );
};

export const PriceControls: FC<IDropDownPrice> = ({
  min,
  max,
  currency,
  isInModal,
  symbol,
}) => {
  const [filterMin, filterMax] = useAppSelector((state) => state.catalog.filteredPrices);

  const { t } = useTranslation("index");
  const minPriceRef = useRef<HTMLInputElement>(null);
  const maxPriceRef = useRef<HTMLInputElement>(null);
  const { setParam, query, router } = useCatalogFilter();
  const { minPrice: queryMin, maxPrice: queryMax } = query;
  const queryButton = query.priceButton;

  const setButtonPrice = (buttonValue: "1" | "2" | "3" | "4") => {
    const oldEntry = query.priceButton;
    if (!oldEntry) {
      return setParam(undefined, {
        maxPrice: undefined,
        minPrice: undefined,
        priceButton: buttonValue,
      });
    }
    if (Array.isArray(oldEntry)) {
      const newValue = oldEntry.includes(buttonValue)
        ? oldEntry.filter((v) => v !== buttonValue)
        : [...oldEntry, buttonValue];

      return setParam(undefined, {
        maxPrice: undefined,
        minPrice: undefined,
        priceButton: newValue,
      });
    }
    if (typeof oldEntry === "string" && oldEntry === buttonValue) {
      return setParam(undefined, {
        maxPrice: undefined,
        minPrice: undefined,
        priceButton: undefined,
      });
    }
    if (typeof oldEntry === "string" && oldEntry !== buttonValue) {
      return setParam(undefined, {
        maxPrice: undefined,
        minPrice: undefined,
        priceButton: [oldEntry, buttonValue],
      });
    }
    return setParam(undefined, {
      maxPrice: undefined,
      minPrice: undefined,
      priceButton: buttonValue,
    });
  };
  const { price1, price2, price3, price4 } = predefinedPrices[currency];

  return (
    <div className={cl(styles.price__popup, isInModal && styles["price__popup--modal"])}>
      <div className={styles.price__inputs}>
        <NumericFormat
          thousandSeparator=" "
          value={queryMin ? +queryMin : filterMin}
          customInput={PriceInput}
          currency={currency}
          suppressHydrationWarning
          getInputRef={minPriceRef}
          valueIsNumericString
          placeholder={toMoney(min)}
          onValueChange={(values, { source }) => {
            if (source === "prop") return;
          }}
          onBlur={(e) => {
            const otherValue = queryMax ? +queryMax : filterMax;
            const curValue = +e.target.value.replaceAll(" ", "");
            if (curValue < otherValue) {
              setParam(undefined, { minPrice: String(curValue), priceButton: undefined });
            } else {
              setParam(undefined, {
                minPrice: String(otherValue - 1),
                priceButton: undefined,
              });
            }
          }}
        />
        <P2>–</P2>
        <NumericFormat
          thousandSeparator=" "
          value={queryMax ? +queryMax : filterMax}
          customInput={PriceInput}
          currency={currency}
          suppressHydrationWarning
          getInputRef={maxPriceRef}
          placeholder={toMoney(max)}
          onValueChange={(values, { source }) => {
            if (source === "prop") return;
          }}
          onBlur={(e) => {
            const curValue = +e.target.value.replaceAll(" ", "");

            const otherValue = queryMin ? +queryMin : filterMin;

            if (curValue > otherValue) {
              setParam(undefined, { maxPrice: String(curValue), priceButton: undefined });
            } else {
              setParam(undefined, {
                maxPrice: String(otherValue + 1),
                priceButton: undefined,
              });
            }
          }}
        />
      </div>
      <Nouislider
        key={Object.entries(router.query).length}
        className={styles.price__slider}
        start={[queryMin ? +queryMin : filterMin - 1, queryMax ? +queryMax : filterMax]}
        connect={[false, true, false]}
        animate={false}
        onSlide={(values) => {
          const numValues = values.map(Number);

          if (numValues.some((v) => v === 0)) return;
          setParam(undefined, {
            ...router.query,
            minPrice: String(numValues[0]),
            maxPrice: String(numValues[1]),
            priceButton: undefined,
          });
        }}
        range={{
          min: [min, 1000],
          "0.01%": [1000, 1000],
          "50%": [predefinedPrices[currency].price4.value, 200_000],
          max: [max, 200_000],
        }}
      />
      <div className={styles.price__buttons}>
       <ToggleButton
          suppressHydrationWarning
          label={`${t("prepositions.lessThan")} ${price1.display.replace(
            "_symbol_",
            symbol,
          )}${currency !== "usd" ? t("prepositions.million") : ""}`}
          disabled={min >= price1.value}
          theme="dark"
          size="md"
          as="button"
          isActive={queryButton === "1" || queryButton?.includes("1")}
          className={cl(
            styles.price__button,
            (queryButton === "1" || queryButton?.includes("1")) &&
              styles["price__button--active"],
          )}
          onClick={() => setButtonPrice("1")}
        />
        <ToggleButton
          suppressHydrationWarning
          label={`${price2.display.replaceAll("_symbol_", symbol)}${
            currency !== "usd" ? t("prepositions.million") : ""
          }`}
          theme="dark"
          size="md"
          as="button"
          isActive={queryButton === "2" || queryButton?.includes("2")}
          className={styles.price__button}
          onClick={() => setButtonPrice("2")}
        />
        <ToggleButton
          suppressHydrationWarning
          label={`${price3.display.replaceAll("_symbol_", symbol)}${t(
            "prepositions.million",
          )}`}
          theme="dark"
          size="md"
          as="button"
          isActive={queryButton === "3" || queryButton?.includes("3")}
          className={styles.price__button}
          onClick={() => setButtonPrice("3")}
        />
        <ToggleButton
          suppressHydrationWarning
          label={`${symbol}${price4.display}${t("prepositions.million")}`}
          theme="dark"
          disabled={max < price4.value}
          size="md"
          as="button"
          isActive={queryButton === "4" || queryButton?.includes("4")}
          className={styles.price__button}
          onClick={() => setButtonPrice("4")}
        /> 
      </div>
    </div>
  );
};
